import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { subscriptionApi } from '../../services/subscriptionApi';
import './Pricing.css';

export default function SubscriptionPage() {
  const navigate = useNavigate();
  const [subscription, setSubscription] = useState(null);
  const [usage, setUsage] = useState(null);
  const [payments, setPayments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [cancelling, setCancelling] = useState(false);

  useEffect(() => {
    const load = async () => {
      try {
        const [currentRes, paymentsRes] = await Promise.all([
          subscriptionApi.getCurrent(),
          subscriptionApi.listPayments(),
        ]);
        setSubscription(currentRes?.subscription || null);
        setUsage(currentRes?.usage || null);
        setPayments(paymentsRes?.payments || []);
      } catch (e) {
        console.error('Failed to load subscription', e);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, []);

  const handleCancel = async () => {
    if (!window.confirm('Are you sure you want to cancel your subscription? You will lose access at the end of your current billing period.')) {
      return;
    }
    setCancelling(true);
    try {
      await subscriptionApi.cancel();
      setSubscription(prev => prev ? { ...prev, status: 'cancelled' } : null);
    } catch (e) {
      alert('Failed to cancel subscription. Please try again.');
    } finally {
      setCancelling(false);
    }
  };

  if (loading) {
    return (
      <div className="subscription-page">
        <div className="pricing-loading">Loading subscription...</div>
      </div>
    );
  }

  const statusLabel = {
    trialing: 'Free Trial',
    active: 'Active',
    suspended: 'Suspended',
    cancelled: 'Cancelled',
    expired: 'Expired',
    past_due: 'Past Due',
  };

  const statusClass = {
    trialing: 'status-trial',
    active: 'status-active',
    suspended: 'status-warning',
    cancelled: 'status-inactive',
    expired: 'status-inactive',
    past_due: 'status-warning',
  };

  const usagePercent = usage
    ? Math.min(100, Math.round((usage.queryCount / usage.queryLimit) * 100))
    : 0;

  return (
    <div className="subscription-page">
      <div className="sub-header">
        <h1>Subscription & Billing</h1>
        {(!subscription || subscription.status === 'trialing' || subscription.status === 'cancelled') && (
          <button className="upgrade-btn" onClick={() => navigate('/user-home/pricing')}>
            {subscription?.status === 'trialing' ? 'Upgrade Plan' : 'View Plans'}
          </button>
        )}
      </div>

      {/* Current Plan */}
      <div className="sub-section">
        <h2>Current Plan</h2>
        {subscription ? (
          <div className="plan-info-card">
            <div className="plan-info-header">
              <div>
                <span className="plan-info-name">{subscription.planName || 'Free Trial'}</span>
                <span className={`plan-status ${statusClass[subscription.status] || ''}`}>
                  {statusLabel[subscription.status] || subscription.status}
                </span>
              </div>
              {subscription.monthlyPriceCents > 0 && (
                <span className="plan-info-price">
                  ${(subscription.monthlyPriceCents / 100).toFixed(0)}/mo
                </span>
              )}
            </div>

            {subscription.status === 'trialing' && subscription.trialEndDate && (
              <div className="trial-info">
                Trial ends: {new Date(subscription.trialEndDate).toLocaleDateString()}
              </div>
            )}

            {subscription.currentPeriodEnd && subscription.status === 'active' && (
              <div className="period-info">
                Current period ends: {new Date(subscription.currentPeriodEnd).toLocaleDateString()}
              </div>
            )}

            {subscription.paymentMethod && (
              <div className="payment-method-info">
                Payment: {subscription.paymentMethod === 'card'
                  ? `Card ending in ${subscription.cardLast4 || '****'}`
                  : 'PayPal'}
              </div>
            )}

            {subscription.status === 'active' && (
              <button className="cancel-btn" onClick={handleCancel} disabled={cancelling}>
                {cancelling ? 'Cancelling...' : 'Cancel Subscription'}
              </button>
            )}
          </div>
        ) : (
          <div className="no-subscription">
            <p>No active subscription. Start your journey with a plan.</p>
            <button className="upgrade-btn" onClick={() => navigate('/user-home/pricing')}>
              View Plans
            </button>
          </div>
        )}
      </div>

      {/* Usage */}
      {usage && (
        <div className="sub-section">
          <h2>Usage This Period</h2>
          <div className="usage-card">
            <div className="usage-header">
              <span>{usage.queryCount} / {usage.queryLimit} queries used</span>
              <span className="usage-percent">{usagePercent}%</span>
            </div>
            <div className="usage-bar-bg">
              <div
                className={`usage-bar-fill ${usagePercent >= 90 ? 'danger' : usagePercent >= 70 ? 'warning' : ''}`}
                style={{ width: `${usagePercent}%` }}
              ></div>
            </div>
            <div className="usage-period">
              {new Date(usage.periodStart).toLocaleDateString()} — {new Date(usage.periodEnd).toLocaleDateString()}
            </div>
          </div>
        </div>
      )}

      {/* Payment History */}
      {payments.length > 0 && (
        <div className="sub-section">
          <h2>Payment History</h2>
          <table className="payments-table">
            <thead>
              <tr>
                <th>Date</th>
                <th>Amount</th>
                <th>Status</th>
                <th>Transaction</th>
              </tr>
            </thead>
            <tbody>
              {payments.map((p) => (
                <tr key={p.paymentId}>
                  <td>{new Date(p.paidAt).toLocaleDateString()}</td>
                  <td>${(p.amountCents / 100).toFixed(2)} {p.currency}</td>
                  <td>
                    <span className={`payment-status ${p.status === 'completed' ? 'success' : 'pending'}`}>
                      {p.status}
                    </span>
                  </td>
                  <td className="txn-id">{p.paypalTransactionId || '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
